import React, { useRef, useState } from "react";
import "./UserComponent.css";
import CloseIcon from "@mui/icons-material/Close";
import ProfileContent from "../../molecules/profileContent/ProfileContent";
import HelpContent from "../../molecules/helpContent/HelpContent";
import SettingsContent from "../../molecules/settingsContent/SettingsContent";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import UserHistoricEventsPage from "../../molecules/userHistoricEventsPage/UserHistoricEventsPage";
import Draggable from "react-draggable";
import { useAccessRights } from "../../../hooks/userPermissions/useAccessRights";

const UserComponent = ({
  onShowUserComponent,
  logout,
  updateProfile,
  updatePassword,
  triggerTooltip,
  scrollZoom,
  setScrollZoom,
  dragRotate,
  setDragRotate,
  keyboard,
  setKeyboard,
  showClock,
  setShowClock,
  showCursorCoords,
  setShowCursorCoords,
  isSmallScreen,
  showUserComponent,
  onShowNavbarMenu,
  onShowUserHistoricEvents,
  userHistoricEventsList,
  handleEventSelect,
  activeTab,
  setActiveTab,
  loadingEventId,
}) => {
  const { canAccessLayer } = useAccessRights();
  const canAccessHistoric = canAccessLayer("physical_model", "historic");
  const toggleSection = (section) => {
    if (section === "eventsHistoric" && !canAccessHistoric) return;
    setActiveTab((prev) => (prev === section ? "" : section)); // toggle open/close on manual click
  };
  const returnToMenu = () => {
    onShowUserComponent();
    onShowNavbarMenu();
  };
  const [activeDrags, setActiveDrags] = useState(0);
  const [deltaPosition, setDeltaPosition] = useState({ x: 50, y: 50 });
  const handleStart = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev + 1);
  };

  const handleDrag = (e, data) => {
    e.stopPropagation();
    e.preventDefault();
    setDeltaPosition({
      x: deltaPosition.x + data.deltaX,
      y: deltaPosition.y + data.deltaY,
    });
  };

  const handleStop = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveDrags((prev) => prev - 1);
  };
  const resizeRef = useRef(null);

  const startResizing = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();

    const element = resizeRef.current;
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = parseInt(
      document.defaultView.getComputedStyle(element).width,
      10,
    );
    const startHeight = parseInt(
      document.defaultView.getComputedStyle(element).height,
      10,
    );

    const doResize = (moveEvent) => {
      if (direction === "horizontal" || direction === "both") {
        const newWidth = startWidth + moveEvent.clientX - startX;
        element.style.width = `${Math.max(newWidth, 300)}px`; // Respect min-width
      }
      if (direction === "vertical" || direction === "both") {
        const newHeight = startHeight + moveEvent.clientY - startY;
        element.style.height = `${Math.max(newHeight, 200)}px`; // Respect min-height
      }
    };

    const stopResizing = () => {
      window.removeEventListener("mousemove", doResize);
      window.removeEventListener("mouseup", stopResizing);
    };

    window.addEventListener("mousemove", doResize);
    window.addEventListener("mouseup", stopResizing);
  };
  return (
    <>
      {isSmallScreen ? (
        <div
          className={`sidebar ${showUserComponent ? "open" : ""}`}
          data-testid="usercomp-sidebar"
        >
          <div className="drag-comp-header">
            <KeyboardDoubleArrowLeftIcon
              onClick={returnToMenu}
              className="drag-comp-returnIcon"
              data-testid="usercomp-sidebar-return-icon"
            />
            <div className="drag-comp-title">Profil</div>
            <CloseIcon
              onClick={onShowUserComponent}
              className="drag-comp-closeIcon"
              data-testid="usercomp-sidebar-close-icon"
            />
          </div>
          <div>
            <div
              className="sidebar-section"
              data-testid="usercomp-sidebar-section-profile"
            >
              <div
                className="sidebar-section-header"
                onClick={() => toggleSection("profile")}
              >
                profil <span>{activeTab === "profile" ? "▲" : "▼"}</span>
              </div>

              <ProfileContent
                logout={logout}
                updateProfile={updateProfile}
                updatePassword={updatePassword}
                triggerTooltip={triggerTooltip}
                isSmallScreen={isSmallScreen}
                openSection={activeTab}
              />
            </div>
            <div
              className="sidebar-section"
              data-testid="usercomp-sidebar-section-settings"
            >
              <div
                className="sidebar-section-header"
                onClick={() => toggleSection("settings")}
              >
                Paramètres <span>{activeTab === "settings" ? "▲" : "▼"}</span>
              </div>
              <SettingsContent
                scrollZoom={scrollZoom}
                setScrollZoom={setScrollZoom}
                dragRotate={dragRotate}
                setDragRotate={setDragRotate}
                keyboard={keyboard}
                setKeyboard={setKeyboard}
                showClock={showClock}
                setShowClock={setShowClock}
                showCursorCoords={showCursorCoords}
                setShowCursorCoords={setShowCursorCoords}
                openSection={activeTab}
                isSmallScreen={isSmallScreen}
              />
            </div>
            <div
              className="sidebar-section"
              data-testid="usercomp-sidebar-section-historic"
            >
              <div
                className="sidebar-section-header"
                onClick={() => {
                  if (!canAccessHistoric) return;
                  toggleSection("eventsHistoric");
                  onShowUserHistoricEvents();
                }}
                aria-disabled={!canAccessHistoric}
                style={{
                  opacity: canAccessHistoric ? 1 : 0.4,
                  cursor: canAccessHistoric ? "pointer" : "not-allowed",
                  pointerEvents: canAccessHistoric ? "auto" : "none",
                }}
              >
                Historique des événements
                <span>{activeTab === "eventsHistoric" ? "▲" : "▼"}</span>
              </div>
              <UserHistoricEventsPage
                openSection={activeTab}
                userHistoricEventsList={userHistoricEventsList}
                isSmallScreen={isSmallScreen}
                handleEventSelect={handleEventSelect}
                loadingEventId={loadingEventId}
              />
            </div>
            <div
              className="sidebar-section"
              data-testid="usercomp-sidebar-section-help"
            >
              <div
                className="sidebar-section-header"
                onClick={() => toggleSection("help")}
              >
                Aide <span>{activeTab === "help" ? "▲" : "▼"}</span>
              </div>
              <HelpContent
                openSection={activeTab}
                isSmallScreen={isSmallScreen}
              />
            </div>
          </div>
        </div>
      ) : (
        <Draggable
          handle=".handle"
          defaultPosition={{ x: -50, y: 50 }}
          position={null}
          grid={[25, 25]}
          scale={1}
          onStart={handleStart}
          onDrag={handleDrag}
          onStop={handleStop}
        >
          <div
            className="user-comp"
            data-testid="usercomp-desktop-panel"
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            ref={resizeRef}
          >
            <div className="user-comp-header handle">
              <div className="user-tabs" data-testid="usercomp-desktop-tabs">
                <div
                  className={`tab ${activeTab === "profile" ? "active" : ""}`}
                  onClick={() => setActiveTab("profile")}
                  data-testid="usercomp-tab-profile"
                >
                  Profil
                </div>
                <div
                  className={`tab ${activeTab === "settings" ? "active" : ""}`}
                  onClick={() => setActiveTab("settings")}
                  data-testid="usercomp-tab-settings"
                >
                  Paramètres
                </div>
                <div
                  className={`tab ${
                    activeTab === "eventsHistoric" ? "active" : ""
                  } ${!canAccessHistoric ? "tab-disabled" : ""}`}
                  onClick={() => {
                    if (!canAccessHistoric) return;
                    setActiveTab("eventsHistoric");
                    onShowUserHistoricEvents();
                  }}
                  aria-disabled={!canAccessHistoric}
                  data-testid="usercomp-tab-historic"
                  style={{
                    opacity: canAccessHistoric ? 1 : 0.4,
                    cursor: canAccessHistoric ? "pointer" : "not-allowed",
                    pointerEvents: canAccessHistoric ? "auto" : "none",
                  }}
                >
                  Historique des événements
                </div>
                <div
                  className={`tab ${activeTab === "help" ? "active" : ""}`}
                  onClick={() => setActiveTab("help")}
                  data-testid="usercomp-tab-help"
                >
                  Aide
                </div>
              </div>
              <CloseIcon
                onClick={onShowUserComponent}
                className="user-comp-closeIcon"
                data-testid="usercomp-desktop-close-icon"
              />
            </div>
            <div className="user-comp-body" data-testid="usercomp-desktop-body">
              {activeTab === "profile" && (
                <ProfileContent
                  logout={logout}
                  updateProfile={updateProfile}
                  updatePassword={updatePassword}
                  triggerTooltip={triggerTooltip}
                  isSmallScreen={isSmallScreen}
                />
              )}
              {activeTab === "settings" && (
                <SettingsContent
                  scrollZoom={scrollZoom}
                  setScrollZoom={setScrollZoom}
                  dragRotate={dragRotate}
                  setDragRotate={setDragRotate}
                  isSmallScreen={isSmallScreen}
                  keyboard={keyboard}
                  setKeyboard={setKeyboard}
                  showClock={showClock}
                  setShowClock={setShowClock}
                  showCursorCoords={showCursorCoords}
                  setShowCursorCoords={setShowCursorCoords}
                />
              )}
              {activeTab === "eventsHistoric" && (
                <UserHistoricEventsPage
                  openSection={activeTab}
                  isSmallScreen={isSmallScreen}
                  userHistoricEventsList={userHistoricEventsList}
                  handleEventSelect={handleEventSelect}
                  loadingEventId={loadingEventId}
                />
              )}
              {activeTab === "help" && (
                <HelpContent
                  openSection={activeTab}
                  isSmallScreen={isSmallScreen}
                />
              )}
            </div>
            <div
              className="resize-handle resize-handle-right"
              onMouseDown={(e) => startResizing(e, "horizontal")}
              data-testid="usercomp-resize-right"
            />
            <div
              className="resize-handle resize-handle-bottom"
              onMouseDown={(e) => startResizing(e, "vertical")}
              data-testid="usercomp-resize-bottom"
            />
            <div
              className="resize-handle resize-handle-corner"
              onMouseDown={(e) => startResizing(e, "both")}
              data-testid="usercomp-resize-corner"
            />
          </div>
        </Draggable>
      )}
    </>
  );
};

export default UserComponent;
